import React, { useState, useEffect, useRef } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { Maximize2, X, ChevronRight, ChevronLeft, Loader2, ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionHeading from '../landing-page/campaigns/components/SectionHeading';
import Macy from 'macy';

gsap.registerPlugin(ScrollTrigger);

// Gallery files that have a case study page: the tile links there instead
// of opening the lightbox (a real <a>, so the page is crawlable from here).
const CASE_STUDY_LINKS = {
  'minimal-booking-app.mp4': 'minimal-bar',
  'minimal-website.mp4': 'minimal-bar',
};

// Project names for tiles that have one (alt text, tracking, hover pill).
const PROJECT_NAMES = {
  'minimal-booking-app.mp4': 'Minimal Booking App',
  'minimal-website.mp4': 'Minimal Website',
};

const GalleryItem = ({ item, color, lang, onSelect, onLoadComplete }) => {
  const caseStudy = CASE_STUDY_LINKS[item.file];
  const href = caseStudy ? `/${lang === 'en' ? 'en' : 'de'}/case-studies/${caseStudy}` : null;
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const handleTrackClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'project_click', {
        project_file: item.file,
        project_label: item.label,
        is_video: item.isVideo
      });
    }
  };

  const handleLoaded = () => {
    // Wait for the next render frame to ensure everything is painted
    requestAnimationFrame(() => {
      setLoading(false);
      if (onLoadComplete) {
        onLoadComplete();
      }
    });
  };

  // Performance Optimization: Only play, decode and render when visible in viewport
  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!item.isVideo || !video || !container) return;

    const isMobile = window.innerWidth < 768;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Play and buffer only when visible in viewport
            video.play().catch(() => {});
          } else {
            // Pause immediately and stop decoding when scrolled out of view
            video.pause();
          }
        });
      },
      {
        root: null, // Viewport
        rootMargin: isMobile ? '50px' : '300px', // Significantly reduced margin on mobile to avoid heavy decoding
        threshold: 0.01 // Trigger as soon as 1px is visible
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (video) video.pause();
    };
  }, [item.isVideo]);

  const Wrapper = href ? 'a' : 'div';

  return (
    <Wrapper
      ref={containerRef}
      href={href || undefined}
      aria-label={href ? `Case Study: ${item.label}` : undefined}
      className={`gallery-item group relative overflow-hidden rounded-lg bg-surface-color/10 border border-white/10 hover:border-white/25 transition-colors duration-500 ${href ? 'cursor-pointer' : 'cursor-zoom-in'} w-full mb-4 min-h-[150px] flex items-center justify-center ${window.innerWidth > 768 ? 'will-change-transform' : ''}`}
      onClick={() => {
        handleTrackClick();
        if (!href) onSelect(item);
      }}
    >
      {/* Loading Spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-color/20 z-10 backdrop-blur-[2px]">
          <Loader2 className="animate-spin text-text-color" size={24} style={{ color }} />
        </div>
      )}

      {item.isVideo ? (
        <video 
          ref={videoRef}
          src={item.src} 
          loop 
          muted 
          playsInline 
          preload="metadata" // Do not preload the full video streams to save bandwith, memory, and CPU
          onLoadedData={handleLoaded}
          onCanPlayThrough={handleLoaded}
          className={`w-full h-auto block transition-all duration-1000 scale-[1.01] group-hover:scale-105 ${loading ? 'opacity-0 scale-[0.96]' : 'opacity-100'}`} 
        />
      ) : (
        <img 
          src={item.src} 
          alt={item.label} 
          loading="lazy" 
          onLoad={handleLoaded}
          className={`w-full h-auto block transition-all duration-1000 scale-[1.01] group-hover:scale-105 ${loading ? 'opacity-0 scale-[0.96]' : 'opacity-100'}`} 
        />
      )}
      
      {/* Overlay details - centered Maximize icon (or a Case Study pill for
          linked projects), no label tags on hover */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center pointer-events-none z-10">
        {href ? (
          <div className="flex items-center gap-2 h-12 px-5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[11px] font-black uppercase tracking-widest scale-90 group-hover:scale-100 transition-transform duration-300">
            {PROJECT_NAMES[item.file] ? `${PROJECT_NAMES[item.file]} · ` : ''}Case Study <ArrowUpRight size={16} />
          </div>
        ) : (
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white scale-90 group-hover:scale-100 transition-transform duration-300">
            <Maximize2 size={18} />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

const MasonryGallery = ({ type = 'design', theme, color, lang, title, highlight, label, description, marqueeLogos = [] }) => {
  const [items, setItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(12);
  const [activeMedia, setActiveMedia] = useState(null);
  const galleryRef = useRef(null);
  const macyInstance = useRef(null);
  const macyId = React.useId().replace(/:/g, ''); // Generate unique ID for each instance

  const de = lang === 'de';

  const LABEL_MAPPING = {
    'action-werk.mp4': { design: de ? 'Branding & Identität' : 'Branding & Identity', ai: de ? 'Prozess-Automatisierung' : 'Process Automation' },
    'bewerbung.ai.mp4': { design: de ? 'UI/UX Design' : 'UI/UX Design', ai: de ? 'KI Plattform' : 'AI Platform' },
    'bluelake-strategy.mp4': { design: de ? 'Plattform-Strategie' : 'Platform Strategy', ai: de ? 'KI-Consulting' : 'AI Consulting' },
    'cre8now-digital-concierge.mp4': { design: de ? 'Interaction Design' : 'Interaction Design', ai: de ? 'Voice AI Agent' : 'Voice AI Agent' },
    'cre8now-driiiv.mp4': { design: de ? '3D Web Experience' : '3D Web Experience', ai: de ? 'Echtzeit-Renderings' : 'Real-time Rendering' },
    'cre8now-editorial.mp4': { design: de ? 'Editorial Design' : 'Editorial Design', ai: de ? 'Generative Assets' : 'Generative Assets' },
    'cre8now-helium.mp4': { design: de ? 'Cineastischer Look' : 'Cineastischer Look', ai: de ? 'KI-Film-Produktion' : 'AI Video Production' },
    'cre8now-midprompts.mp4': { design: de ? 'Visual Concept' : 'Visual Concept', ai: de ? 'Prompt Engineering' : 'Prompt Engineering' },
    'cre8now-teaser.mp4': { design: de ? 'Motion Design' : 'Motion Design', ai: de ? 'Schnitt & Animation' : 'Editing & Animation' },
    'cre8now-work_1.mp4': { design: de ? 'Webflow Development' : 'Webflow Development', ai: de ? 'API Integration' : 'API Integration' },
    'markuswentlandt.mp4': { design: de ? 'Portfolio Design' : 'Portfolio Design', ai: de ? 'Custom KI-System' : 'Custom AI System' },
    'meethelix.mp4': { design: de ? 'Product Design' : 'Product Design', ai: de ? 'Workflow Optimierung' : 'Workflow Optimization' },
    'tinycomp.mp4': { design: de ? 'Tool & Produkt Design' : 'Tool & Product Design', ai: de ? 'Lokale Automatisierung' : 'Local Automation' },
    'valet.mp4': { design: de ? 'Design System' : 'Design System', ai: de ? 'Datenanalyse' : 'Data Analytics' }
  };

  useEffect(() => {
    const loadManifest = async () => {
      try {
        let manifest;
        try {
          // 1. Try to fetch dynamically from PHP API first (great for immediate updates on the live server)
          const response = await fetch(`/api/manifest.php?t=${Date.now()}`);
          if (!response.ok) throw new Error('PHP manifest endpoint failed');
          manifest = await response.json();
        } catch (apiErr) {
          // 2. Fall back to static JSON manifest (used for local dev, static hosting, or fallback)
          const response = await fetch(`/assets/work-manifest.json?t=${Date.now()}`);
          manifest = await response.json();
        }

        const files = type === 'ai' ? manifest.ai : [...manifest.design];
        // The design gallery opens in a fresh order on every visit, so no
        // project is permanently buried below the fold (Fisher–Yates).
        if (type !== 'ai') {
          for (let i = files.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [files[i], files[j]] = [files[j], files[i]];
          }
          // Newest projects lead the shuffled grid so they're always in the
          // first batch instead of landing behind "load more".
          const PINNED = ['minimal-booking-app.mp4', 'minimal-website.mp4'];
          for (const name of [...PINNED].reverse()) {
            const at = files.indexOf(name);
            if (at > 0) files.unshift(...files.splice(at, 1));
          }
        }
        const folder = type === 'ai' ? 'ai-work' : 'design-work';
        const parsed = files.map((file, index) => {
          const isVideo = /\.(mp4|webm|mov)$/i.test(file);
          
          // User requested specific tag format: "CRE8NOW - AI - DESIGN - 01"
          const paddedIndex = String(index + 1).padStart(2, '0');
          const numberedTag = `CRE8NOW - AI - DESIGN - ${paddedIndex}`;

          return {
            src: `/assets/${folder}/${file}`,
            label: PROJECT_NAMES[file] || numberedTag,
            isVideo,
            file
          };
        });
        setItems(parsed);
      } catch (err) {
        console.error('Failed to load work manifest:', err);
      }
    };
    loadManifest();
  }, [type]);

  // Initialize and Update Macy
  useEffect(() => {
    if (items.length === 0) return;

    let macy;

    const init = () => {
      const containerSelector = `#macy-container-${macyId}`;
      const container = document.querySelector(containerSelector);
      
      if (!container || container.offsetWidth === 0) {
        // If container is not visible yet, retry once
        setTimeout(init, 100);
        return;
      }

      // Initialize Macy ONLY once if possible, else recalculate
      if (!macyInstance.current) {
        macy = Macy({
          container: containerSelector,
          trueOrder: false,
          waitForImages: true,
          debug: false,
          margin: 24, // Desktop base margin
          columns: 3,
          breakAt: {
            1400: {
              columns: 3,
              margin: 20
            },
            1200: {
              columns: 3,
              margin: 16
            },
            940: {
              columns: 2,
              margin: 12
            },
            520: {
              columns: 1,
              margin: 12
            }
          }
        });
        macyInstance.current = macy;
      } else {
        macy = macyInstance.current;
        macy.recalculate(true, true); // TrueOrder update to keep columns stable
      }
      
      const refresh = () => {
        if (macyInstance.current) {
          macyInstance.current.recalculate(true, true);
          // Optimization: Avoid frequent ScrollTrigger refreshes on mobile to prevent scroll locking
          if (window.innerWidth >= 768) {
            ScrollTrigger.refresh();
          }
        }
      };

      // Recalculate on image load and on recalculate event
      macy.on(macy.constants.EVENT_IMAGE_LOAD, refresh);
      macy.on(macy.constants.EVENT_RECALCULATED, () => {
        if (window.innerWidth >= 768) {
          ScrollTrigger.refresh();
        }
      });

      // Initial run and repeat runs to handle dynamically rendered media / tab switching
      refresh();
      // On mobile, only do one initial refresh after a longer delay to avoid fighting the first scroll
      if (window.innerWidth < 768) {
        setTimeout(refresh, 1000);
      } else {
        [50, 150, 300, 600, 1000, 1500, 2000].forEach(delay => setTimeout(refresh, delay));
      }
    };

    const timer = setTimeout(init, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [items, visibleCount, type, macyId]);

  // Callback from individual GalleryItems as soon as their height is known
  const handleItemLoadComplete = () => {
    if (macyInstance.current) {
      macyInstance.current.recalculate(true, true);
      // Optimization: Avoid frequent ScrollTrigger refreshes on mobile
      if (window.innerWidth >= 768) {
        setTimeout(() => {
          ScrollTrigger.refresh();
        }, 50);
      }
    }
  };

  useEffect(() => {
    if (items.length === 0) return;
    
    let ctx;
    let timer;

    const initScrollTriggers = () => {
      if (ctx) ctx.revert();
      
      ctx = gsap.context(() => {
        const isMobile = window.innerWidth < 768;
        const elements = gsap.utils.toArray('.gallery-item');
        
        // Group unique offsetLeft values to dynamically find the correct column index for each item
        const uniqueLefts = [...new Set(elements.map(el => el.offsetLeft))].sort((a, b) => a - b);

        elements.forEach((el, i) => {
          // Identify the item's column based on its actual rendered left position
          const colIndex = uniqueLefts.indexOf(el.offsetLeft);
          
          if (isMobile) {
            // Smooth reveal scrub on mobile/1-column layouts to ensure clear visibility
            gsap.fromTo(el, 
              { opacity: 0, y: 30 },
              { 
                opacity: 1, 
                y: 0, 
                ease: 'none',
                scrollTrigger: {
                  trigger: el,
                  start: "top 98%",
                  end: "top 75%",
                  scrub: 1,
         
                }
              }
            );
          } else {
            // Define column-specific parallax travel distances (pronounced offsets for faster glide)
            const travelDistance = colIndex === 0 ? -90 : colIndex === 1 ? 90 : colIndex === 2 ? -45 : 45;

            // 1. Unified Scroll Scrub (individual trigger): Handles linear scroll-scrub fade and entrance translate
            gsap.fromTo(el,
              { opacity: 0, y: 60 },
              { 
                opacity: 1, 
                y: 0,
                ease: 'none',
                scrollTrigger: {
                  trigger: el,
                  start: "top 95%",
                  end: "top 60%",
                  scrub: 1,
                  invalidateOnRefresh: true
                }
              }
            );

            // 2. Continuous Parallax Motion (gallery container trigger): 
            // Slides elements smoothly at slightly different relative offsets on scroll, 
            // after completing their standard scrub fade-in entrance.
            gsap.fromTo(el,
              { yPercent: travelDistance * 0.15 },
              { 
                yPercent: -travelDistance * 0.15,
                ease: 'none',
                scrollTrigger: {
                  trigger: `#macy-container-${macyId}`,
                  start: "top bottom",
                  end: "bottom top",
                  scrub: 1,
                  invalidateOnRefresh: true
                }
              }
            );
          }
        });
      }, galleryRef);
      
      // Force a refresh of ScrollTrigger positions only on Desktop to prevent momentum locks on mobile
      if (window.innerWidth >= 768) {
        ScrollTrigger.refresh();
      }
    };

    // Create ScrollTriggers after a tiny delay to let Macy position things first!
    timer = setTimeout(initScrollTriggers, 200);

    // Watch for window resizes: Re-map offsets and recalculate ScrollTriggers when column layout shifts (ignore on mobile to prevent address bar triggers)
    let resizeTimer;
    const handleResize = () => {
      if (window.innerWidth < 768) return;
      clearTimeout(resizeTimer);
      // Debounce slightly to avoid unnecessary performance overhead during active drag resizing
      resizeTimer = setTimeout(() => {
        initScrollTriggers();
      }, 250);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      if (ctx) ctx.revert();
    };
  }, [items, visibleCount]);

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + 12, items.length));
  };

  // Keyboard navigation & gallery slide transition logic
  const activeMediaIndex = items.findIndex(item => item.src === activeMedia?.src);

  const handleNextMedia = (e) => {
    if (e) e.stopPropagation();
    if (items.length === 0 || activeMediaIndex === -1) return;
    const nextIndex = (activeMediaIndex + 1) % items.length;
    setActiveMedia(items[nextIndex]);
  };

  const handlePrevMedia = (e) => {
    if (e) e.stopPropagation();
    if (items.length === 0 || activeMediaIndex === -1) return;
    const prevIndex = (activeMediaIndex - 1 + items.length) % items.length;
    setActiveMedia(items[prevIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!activeMedia) return;
      if (e.key === 'ArrowRight') handleNextMedia();
      if (e.key === 'ArrowLeft') handlePrevMedia();
      if (e.key === 'Escape') setActiveMedia(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeMedia, activeMediaIndex, items]);

  return (
    <div ref={galleryRef} className="w-full pt-24 pb-24 md:pt-32 md:pb-24 bg-bg-color border-y border-border-color/10">
      <div className="w-full px-4">
        <div className="w-full px-4 mb-16 md:mb-24">
          <SectionHeading title={title} highlight={highlight} color={color} label={label} description={description} centered={true} theme={theme} />
        </div>

        {/* Optional Marquee Bar */}
        {marqueeLogos && marqueeLogos.length > 0 && (
          <div
            className="w-full mb-16 md:mb-24 overflow-hidden"
            ref={(el) => {
              if (el && typeof window !== 'undefined') {
                const observer = new IntersectionObserver(([entry]) => {
                  const marquee = el.querySelector('.animate-infinite-scroll');
                  if (marquee) {
                    marquee.setAttribute('data-paused', !entry.isIntersecting);
                  }
                }, { threshold: 0 });
                observer.observe(el);
                return () => observer.disconnect();
              }
            }}
          >
            <div className="flex will-change-transform animate-infinite-scroll">
              {[0, 1].map((set) => (
                <div key={set} className="flex items-center gap-10 md:gap-20 pr-10 md:pr-20 flex-shrink-0" aria-hidden={set === 1 ? true : undefined}>
                  {marqueeLogos.map((logo, i) => (
                    <img 
                      key={i} 
                      src={logo.src} 
                      alt={logo.alt}
                      width={200}
                      height={80}
                      loading="eager"
                      decoding="async"
                      className={`h-12 md:h-20 w-auto object-contain transition-all duration-500 flex-shrink-0 opacity-50 hover:opacity-80
                        ${theme === 'dark' ? '' : 'brightness-0'}
                      `}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Macy.js Container */}
        <div id={`macy-container-${macyId}`} className="w-full relative">
          {items.slice(0, visibleCount).map((item) => (
            <GalleryItem key={item.src} item={item} color={color} lang={lang} onSelect={setActiveMedia} onLoadComplete={handleItemLoadComplete} />
          ))}
        </div>
        {visibleCount < items.length && (
          <div className="flex justify-center mt-16">
            <button onClick={handleShowMore} className="group relative px-10 py-4 font-black uppercase tracking-widest text-[11px] transition-all duration-500 overflow-hidden border border-text-color text-text-color rounded-[4px] inline-flex items-center gap-3">
              <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-text-color" />
              <span className="relative z-10 flex items-center gap-3 group-hover:text-bg-color transition-colors duration-500">
                {de ? 'Mehr zeigen' : 'Show More'}
                <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        )}
      </div>
      <AnimatePresence>
        {activeMedia && (
          <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-3xl p-4 md:p-12" onClick={() => setActiveMedia(null)}>
            <button onClick={() => setActiveMedia(null)} className="absolute top-8 right-8 z-50 p-4 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white">
              <X size={32} />
            </button>

            {/* Navigation Arrows */}
            <button 
              onClick={handlePrevMedia}
              className="absolute left-4 md:left-8 z-50 p-4 rounded-full bg-white/5 hover:bg-white/15 text-white transition-all hover:scale-105 active:scale-95"
            >
              <ChevronLeft size={36} />
            </button>

            <button 
              onClick={handleNextMedia}
              className="absolute right-4 md:right-8 z-50 p-4 rounded-full bg-white/5 hover:bg-white/15 text-white transition-all hover:scale-105 active:scale-95"
            >
              <ChevronRight size={36} />
            </button>

            <Motion.div 
              key={activeMedia.src}
              initial={{ scale: 0.95, opacity: 0, x: 20 }} 
              animate={{ scale: 1, opacity: 1, x: 0 }} 
              exit={{ scale: 0.95, opacity: 0, x: -20 }} 
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-w-[90vw] max-h-[90vh] overflow-hidden rounded-lg shadow-2xl" 
              onClick={(e) => e.stopPropagation()}
            >
              {activeMedia.isVideo ? (
                <video src={activeMedia.src} autoPlay controls playsInline className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
              ) : (
                <img src={activeMedia.src} alt={activeMedia.label} className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
              )}
            </Motion.div>
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MasonryGallery;
